import type { Ref } from "react";
import { createRef, PureComponent } from "react";

import { find, get, identity } from "lodash";
import { connect } from "react-redux";

import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";

import { tableActionsCreator } from "../../../actions/tableActions";

import { SWReactTable } from "./SWReactTable";
import { SWReactTableOptimized } from "./SWReactTableOptimized";
import SWReactTableWrapper from "./SWReactTableWrapper";
import type { ISWReactTableWrapperSelectionContext } from "./SWReactTableWrapperSelectionContext";
import { SWReactTableWrapperContextProvider } from "./SWReactTableWrapperSelectionContext";

type OwnPropsType = {
    tableSelectionKey?: string;
    tableSelectionProperty: string;
    deleteTableSelectionOnUnMount?: boolean;
};
interface IWithSelectionState {
    data: any;
    page: number;
}
interface IWithSelectionProps {
    tableSelectionKey: string;
    tableSelectionProperty: string;
    initialSelectedRows?: any[];
    onBeforeRowSelectionToggle?: (row: FixmeType) => any;
    shouldSelectRow?: (row: FixmeType) => any;
    cleanOnUnMount?: boolean;
    deleteTableSelectionOnUnMount?: boolean;
    maxSelectedRows?: number;
    selectAllCount?: number;
    fetchServerPages?: number;
    getDataCallback?: (data: FixmeType) => void;
    tableData?: any;
    tableRef?: Ref<any>;
    handleClickOutOfMaxSelected?: () => void;
}
const withSelection = (WrappedComponent: FixmeType) =>
    class WithSelection extends PureComponent<
        IWithSelectionProps & ISWReactTableWrapperSelectionContext,
        IWithSelectionState
    > {
        public static defaultProps = {
            onBeforeRowSelectionToggle: (row: FixmeType) => row,
            shouldSelectRow: () => true,
            cleanOnUnMount: true,
            deleteTableSelectionOnUnMount: false,
            maxSelectedRows: undefined,
            selectAllCount: undefined,
            getDataCallback: identity,
            tableRef: createRef(),
        };

        public static getDerivedStateFromProps(
            nextProps: IWithSelectionProps & ISWReactTableWrapperSelectionContext,
            prevState: IWithSelectionState,
        ) {
            // to support different ways to store on backend side
            const dataRecords =
                get(nextProps, "tableData.Records", undefined) || get(nextProps, "tableData.Data", undefined);
            if (dataRecords && dataRecords !== prevState.data.records) {
                return {
                    data: {
                        records: dataRecords,
                    },
                };
            }
            return null;
        }

        constructor(props: IWithSelectionProps & ISWReactTableWrapperSelectionContext) {
            super(props);
            const dataRecords =
                get(this.props, "tableData.Records", undefined) || get(this.props, "tableData.Data", undefined);
            const records = dataRecords ? [dataRecords] : [];
            this.state = {
                data: { records },
                page: 0,
            };
        }

        public componentWillUnmount() {
            if (this.props.cleanOnUnMount) {
                this.props.clearAllSelectedRows();
            }
        }

        public componentDidMount() {
            if (this.props.initialSelectedRows && this.props.selectRows) {
                // Use setTimeout to ensure this happens after any synchronous cleanup
                setTimeout(() => {
                    this.props.selectRows(this.props.initialSelectedRows);
                }, 0);
            }
        }

        private onGetData = (data: FixmeType) => {
            // support different data keys from backend
            const adoptedData =
                data && data.records
                    ? data
                    : {
                          ...data,
                          records: data.Data || data.Records,
                      };
            this.setState({
                data: {
                    ...adoptedData,
                },
            });
            this.props.getDataCallback?.(data);
        };

        private changePage = (page: number) => {
            this.setState({ page });
            if (this.props.changePageCallback) {
                this.props.changePageCallback(page);
            }
        };

        private onRowSelection = (row: FixmeType) => {
            const shouldSelect = this.props.shouldSelectRow?.(row);
            if (!shouldSelect) {
                return;
            }

            if (
                this.props.maxSelectedRows &&
                this.props.selectedRows.length >= this.props.maxSelectedRows &&
                !row.selected
            ) {
                this.props.handleClickOutOfMaxSelected?.();
                return;
            }
            const modifiedRow = this.props.onBeforeRowSelectionToggle?.(row);
            this.props.onRowSelection(modifiedRow);
        };

        private getStoreRow = (row: FixmeType) => {
            const { selectedRows, tableSelectionProperty } = this.props;
            const matchedRow = find(selectedRows, {
                [tableSelectionProperty]: row[tableSelectionProperty],
            });
            return matchedRow;
        };

        private isRowSelected = (row: FixmeType) => {
            const { selectedRows, tableSelectionProperty } = this.props;
            const matchedRow = find(selectedRows, {
                [tableSelectionProperty]: row[tableSelectionProperty],
            });
            return !!matchedRow;
        };

        private allRowsSelected = () => this.state?.data?.records?.length && this.checkIfAllRowsSelected();

        private checkIfAllRowsSelected = () => {
            const selectableRecords = this.state.data.records.filter((row: FixmeType) =>
                this.props.shouldSelectRow?.(row),
            );
            if (this.props.selectAllCount) {
                let page = this.state.page || 0;
                const fetchServerPages = this.props.fetchServerPages ?? 1;
                if (page >= fetchServerPages) {
                    page %= fetchServerPages;
                }
                const slicedSelected = selectableRecords.slice(
                    page * this.props.selectAllCount,
                    (page + 1) * this.props.selectAllCount,
                );
                if (slicedSelected.length === 0) {
                    return false;
                }
                return slicedSelected.every(this.isRowSelected);
            } else {
                return selectableRecords.every(this.isRowSelected);
            }
        };

        private onSelectAllRows = () => {
            if (this.allRowsSelected()) {
                this.props.clearAllSelectedRows();
            } else {
                if (this.props.selectAllCount) {
                    this.props.clearAllSelectedRows();
                    let page = this.state.page || 0;
                    const fetchServerPages = this.props.fetchServerPages ?? 1;
                    if (page >= fetchServerPages) {
                        page %= fetchServerPages;
                    }
                    this.props.onSelectAllRows(
                        this.state.data.records
                            .slice(page * this.props.selectAllCount, (page + 1) * this.props.selectAllCount)
                            .filter((row: FixmeType) => this.props.shouldSelectRow?.(row)),
                    );
                } else {
                    this.props.onSelectAllRows(
                        this.state.data.records.filter((row: FixmeType) => this.props.shouldSelectRow?.(row)),
                    );
                }
            }
        };

        public render() {
            const {
                data: { TotalCount, freeTextFilter, filteredResultCount },
            } = this.state;

            const { selectedRows, clearAllSelectedRows, selectRows, tableRef, maxSelectedRows, ...restProps } =
                this.props;

            const onRowSelection = this.onRowSelection;
            const contextValue = {
                selectedRows,
                onRowSelection,
                clearAllSelectedRows,
                isRowSelected: this.isRowSelected,
                allRowsSelected: this.allRowsSelected(),
                onSelectAllRows: this.onSelectAllRows,
                selectRows,
                getStoreRow: this.getStoreRow,
                totalRecord: TotalCount || filteredResultCount,
                textFilter: freeTextFilter,
                maxSelectedRows,
            };
            return (
                <SWReactTableWrapperContextProvider value={contextValue}>
                    <WrappedComponent
                        ref={tableRef}
                        {...restProps}
                        getDataCallback={this.onGetData}
                        changePageCallback={this.changePage}
                    />
                </SWReactTableWrapperContextProvider>
            );
        }
    };

const mapStateToProps = (state: RootState, ownProps: OwnPropsType) => {
    return {
        selectedRows: getTableSelectedItems(ownProps.tableSelectionKey ?? "")(state),
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatchCommon, ownProps: OwnPropsType) => {
    if (ownProps.tableSelectionKey && ownProps.tableSelectionProperty) {
        const { toggleRows, clearAllSelectedRows, selectRows } = tableActionsCreator(
            ownProps.tableSelectionKey,
            ownProps.tableSelectionProperty,
        );

        return {
            onRowSelection: (row: FixmeType) => {
                dispatch(toggleRows([row]));
            },
            selectRows: (rows: FixmeType) => {
                dispatch(selectRows(rows));
            },
            onSelectAllRows: (rows: FixmeType) => {
                dispatch(selectRows(rows));
            },
            clearAllSelectedRows: () => {
                dispatch(clearAllSelectedRows());
            },
        };
    }
    return {};
};

export const SWReactTableWrapperWithSelection = connect(
    mapStateToProps,
    mapDispatchToProps,
    // @ts-ignore FIXME: strict migration - connect's HOC typing rejects withSelection's prop union; legacy redux wiring
)(withSelection(SWReactTableWrapper));

export const SWReactTableWithSelection = connect(
    mapStateToProps,
    mapDispatchToProps,
    // @ts-ignore FIXME: strict migration - connect's HOC typing rejects withSelection's prop union; legacy redux wiring
)(withSelection(SWReactTable));

export const SWReactTableOptimizedWithSelection = connect(
    mapStateToProps,
    mapDispatchToProps,
    // @ts-ignore FIXME: strict migration - connect's HOC typing rejects withSelection's prop union; legacy redux wiring
)(withSelection(SWReactTableOptimized));
